import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiTrendingUp, FiTarget, FiUsers, FiAward, FiCheckCircle } = FiIcons;

const Home = () => {
  const services = [
    {
      title: 'Strategic Planning & Market Entry',
      description: 'Comprehensive roadmaps for sustainable growth within South Africa\'s dynamic economic environment.',
      link: '/services/strategic-planning'
    },
    {
      title: 'Operational Excellence & Process Optimisation',
      description: 'Streamline operations and maximise efficiency while maintaining quality standards.',
      link: '/services'
    },
    {
      title: 'Digital Transformation & Technology Integration',
      description: 'Navigate technology transformation with solutions addressing South Africa\'s unique challenges.',
      link: '/services/digital-transformation'
    },
    {
      title: 'Regulatory Compliance & Risk Management',
      description: 'Expert guidance through complex regulatory frameworks and risk mitigation strategies.',
      link: '/services'
    },
    {
      title: 'Merger & Acquisition Advisory',
      description: 'Strategic guidance for M&A transactions with deep local market expertise.',
      link: '/services'
    },
    {
      title: 'Leadership Development & Change Management',
      description: 'Build leadership capabilities and manage organizational transformation effectively.',
      link: '/services'
    }
  ];

  const stats = [
    { number: '25+', label: 'Years of Excellence' },
    { number: '200+', label: 'Successful Projects' },
    { number: '50+', label: 'JSE Listed Clients' },
    { number: '34%', label: 'Average Efficiency Gain' }
  ];

  const testimonials = [
    {
      quote: "RSA Solutions' strategic guidance was instrumental in our successful JSE listing. Their deep understanding of regulatory requirements combined with practical implementation support made the complex process manageable.",
      author: "CEO, Emerging Mining Company"
    },
    {
      quote: "The operational excellence programme delivered by RSA Solutions transformed our manufacturing efficiency by 34% whilst maintaining our commitment to local employment and skills development.",
      author: "Operations Director, Automotive Parts Manufacturer"
    },
    {
      quote: "Their BEE strategy implementation went beyond compliance, creating genuine partnerships that strengthened our supply chain and opened new market opportunities.",
      author: "Managing Director, Financial Services Group"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-figtree leading-tight">
              Empowering Organisations Through{' '}
              <span className="text-yellow-400">Strategic Excellence</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-gray-200 leading-relaxed">
              RSA Solutions stands as Gauteng's premier management consulting partnership, delivering transformative solutions that drive sustainable growth across diverse industries.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-yellow-400 text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center justify-center"
              >
                Get Strategic Consultation
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-purple-900 transition-colors inline-flex items-center justify-center"
              >
                Explore Our Services
              </Link>
            </div>
          </motion.div>
        </div>
        {/* Geometric pattern overlay */}
        <div className="absolute bottom-0 right-0 w-96 h-96 opacity-10">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <polygon points="50,10 90,90 10,90" fill="currentColor" />
            <polygon points="50,30 70,70 30,70" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold text-purple-900 mb-2 font-figtree">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-figtree">
                Over Two Decades of Strategic Excellence
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                For over two decades, we have partnered with enterprises ranging from emerging JSE-listed companies to established mining conglomerates, helping them navigate complex market dynamics whilst maintaining operational excellence.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our methodology combines international best practices with deep understanding of the South African business landscape, ensuring strategies that resonate within local market conditions whilst positioning clients for global competitiveness.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-purple-700 font-semibold hover:text-purple-800 transition-colors"
              >
                Learn More About Our Story
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Johannesburg skyline"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/20 to-transparent rounded-lg"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-figtree">
              Comprehensive Consulting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver strategic solutions across all aspects of business transformation, from initial planning to successful implementation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow group"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-purple-700 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="inline-flex items-center text-purple-700 font-semibold hover:text-purple-800 transition-colors"
                >
                  Learn More
                  <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-figtree">
              Client Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Hear from leaders who have transformed their organizations with our guidance
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-purple-50 p-8 rounded-xl"
              >
                <blockquote className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                <div className="font-semibold text-purple-900">
                  — {testimonial.author}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 font-figtree">
              Ready to Transform Your Organisation?
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Let's discuss how our strategic expertise can drive sustainable growth for your business.
            </p>
            <Link
              to="/contact"
              className="bg-yellow-400 text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center"
            >
              Schedule Your Consultation
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;